import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PROTOCOL_STEPS } from '../accidentProtocol';
import AttachButton, { AttachThumb, isPdf } from '../components/AttachButton';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`); return d; });
}

export default function ReportAccident() {
  const navigate = useNavigate();
  const { worker } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [showProtocol, setShowProtocol] = useState(true);
  const [f, setF] = useState({
    equipment_id: '', occurred_at: new Date().toISOString().slice(0, 16), location: '', lat: null, lng: null,
    description: '', injuries: 0, injury_details: '', police_responded: 0, police_agency: '', officer: '', report_number: '',
    other_party: '', other_phone: '', other_insurance: '', other_vehicle: '', witnesses: '', our_damage: '',
  });
  const [pending, setPending] = useState([]);
  const [gpsMsg, setGpsMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch('/fuel-trucks').then(list => {
      const arr = Array.isArray(list) ? list : [];
      setTrucks(arr);
      const myName = `${worker?.firstName || ''} ${worker?.lastName || ''}`.trim().toLowerCase();
      const mine = myName && arr.find(t => (t.assigned_to || '').trim().toLowerCase() === myName);
      if (mine) set('equipment_id', mine.id);
    }).catch(() => {});
  }, []);

  function set(k, v) { setF(s => ({ ...s, [k]: v })); }
  function useMyLocation() {
    if (!navigator.geolocation) { setGpsMsg('Location not available'); return; }
    setGpsMsg('Locating…');
    navigator.geolocation.getCurrentPosition(
      p => { set('lat', p.coords.latitude); set('lng', p.coords.longitude); setGpsMsg(`📍 Location captured (${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)})`); },
      () => setGpsMsg('Could not get location'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  async function submit() {
    if (!f.description.trim()) { setErr('Describe what happened'); return; }
    setSubmitting(true); setErr('');
    try {
      const saved = await apiFetch('/accident-events', { method: 'POST', body: JSON.stringify(f) });
      if (!saved || !saved.id) throw new Error('Save failed');
      for (const file of pending) {
        const fd = new FormData(); fd.append('file', file);
        await fetch(`${API_BASE}/accident-events/${saved.id}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      }
      setDone(true);
    } catch (e) { setErr('Could not submit — try again'); } finally { setSubmitting(false); }
  }

  const inp = { border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: '12px', fontSize: 16, width: '100%', boxSizing: 'border-box', background: 'var(--card, #fff)', color: 'var(--text)' };
  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--gray-muted, #6b7280)', textTransform: 'uppercase', marginBottom: 6, display: 'block' };
  const sec = { fontSize: 12, fontWeight: 800, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 8, marginBottom: 4 };
  const F = (label, k, opts = {}) => (
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>{label}</label>
      {opts.textarea
        ? <textarea value={f[k]} onChange={e => set(k, e.target.value)} rows={opts.rows || 2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
        : <input type={opts.type || 'text'} value={f[k]} onChange={e => set(k, e.target.value)} style={inp} />}
    </div>
  );

  if (done) {
    return (
      <div className="page">
        <div className="top-bar"><div className="top-bar-wordmark">TITAN <span>ELECTRIC</span></div></div>
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: 54, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 6 }}>Accident reported</div>
          <div style={{ fontSize: 14, color: 'var(--gray-muted)', marginBottom: 28 }}>The office has been notified. Follow the on-scene steps and stay safe.</div>
          <button onClick={() => navigate('/dashboard')} style={{ width: '100%', maxWidth: 320, padding: 14, background: '#f97316', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="top-bar">
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', color: '#fff', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Back</span>
        </button>
      </div>

      <div style={{ padding: '18px 16px 100px' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 12 }}>🚨 Report Accident</div>

        {/* Protocol reminder */}
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, marginBottom: 18, overflow: 'hidden' }}>
          <button onClick={() => setShowProtocol(v => !v)} style={{ width: '100%', background: 'none', border: 'none', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
            <span style={{ fontWeight: 800, fontSize: 13, color: '#991b1b' }}>FIRST — DO THIS</span>
            <span style={{ fontSize: 12, color: '#b91c1c' }}>{showProtocol ? '▲' : '▼'}</span>
          </button>
          {showProtocol && (
            <ol style={{ margin: 0, padding: '0 20px 14px 34px', color: '#7f1d1d', fontSize: 13, lineHeight: 1.6 }}>
              {PROTOCOL_STEPS.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
            </ol>
          )}
        </div>

        <div style={sec}>Scene</div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Truck</label>
          <select value={f.equipment_id} onChange={e => set('equipment_id', e.target.value)} style={inp}>
            <option value="">— Select truck —</option>
            {trucks.map(t => <option key={t.id} value={t.id}>{t.asset_number ? `${t.asset_number} · ` : ''}{t.name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Date &amp; time</label>
          <input type="datetime-local" value={f.occurred_at} onChange={e => set('occurred_at', e.target.value)} style={inp} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Location</label>
          <input value={f.location} onChange={e => set('location', e.target.value)} style={inp} placeholder="Street / intersection" />
          <button onClick={useMyLocation} style={{ marginTop: 8, padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', background: 'var(--card, #fff)', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>📍 Use my location</button>
          {gpsMsg && <div style={{ fontSize: 12, color: 'var(--gray-muted)', marginTop: 6 }}>{gpsMsg}</div>}
        </div>
        {F('What happened', 'description', { textarea: true, rows: 3 })}

        <div style={sec}>Injuries</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 15, color: 'var(--text)', fontWeight: 600 }}>
          <input type="checkbox" checked={!!f.injuries} onChange={e => set('injuries', e.target.checked ? 1 : 0)} style={{ width: 20, height: 20 }} /> Anyone injured?
        </label>
        {!!f.injuries && F('Injury details', 'injury_details', { textarea: true })}

        <div style={sec}>Police</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 15, color: 'var(--text)', fontWeight: 600 }}>
          <input type="checkbox" checked={!!f.police_responded} onChange={e => set('police_responded', e.target.checked ? 1 : 0)} style={{ width: 20, height: 20 }} /> Police responded?
        </label>
        {!!f.police_responded && (<>{F('Agency', 'police_agency')}{F('Officer name / badge', 'officer')}{F('Report number', 'report_number')}</>)}

        <div style={sec}>Other Party</div>
        {F('Name', 'other_party')}
        {F('Phone', 'other_phone', { type: 'tel' })}
        {F('Insurance co. & policy #', 'other_insurance')}
        {F('Their vehicle & plate', 'other_vehicle')}
        {F('Witnesses (name + phone)', 'witnesses', { textarea: true })}

        <div style={sec}>Damage</div>
        {F('Damage to our truck', 'our_damage', { textarea: true })}

        <div style={sec}>Photos &amp; files</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {pending.map((file, i) => (
              <AttachThumb key={i} size={80} src={isPdf(file) ? null : URL.createObjectURL(file)} name={file.name}
                onRemove={() => setPending(p => p.filter((_, j) => j !== i))} />
            ))}
          </div>
          <AttachButton multiple icon="📷" onFiles={fs => setPending(p => [...p, ...fs])} />
        </div>

        {err && <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{err}</div>}
        <button onClick={submit} disabled={submitting}
          style={{ width: '100%', padding: 16, background: submitting ? '#e0a3a3' : '#dc2626', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
          {submitting ? 'Submitting…' : 'Submit Report'}
        </button>
      </div>
    </div>
  );
}
